import React, { useState, useEffect } from "react"
import Papa from "papaparse"
import config from '../config.js';
import { apiErrorMessage } from '../utils/apiError';

/**
 * AdminDataTransferPage
 * Dynamically fetches table names from /tables, then
 * provides import/export functionality for whichever table is selected.
 */
export default function AdminDataTransferPage() {
  // Instead of a static DATASETS array, we’ll store dynamic tables in state
  const [tables, setTables] = useState([])            // Array of strings or objects
  const [selectedTable, setSelectedTable] = useState("")  // The chosen table
  const [file, setFile] = useState(null)
  const [previewData, setPreviewData] = useState([])
  const [previewHeaders, setPreviewHeaders] = useState([])
  const [showPreview, setShowPreview] = useState(false)

  const [message, setMessage] = useState("")
  const [error, setError] = useState("")

  // Adjust for your environment
  const API_BASE = config.API_BASE_URL;

  /**
   * Fetch the list of tables on first render.
   */
  useEffect(() => {
    async function fetchTables() {
      try {
        setError("")
        setMessage("")
        const res = await fetch(`${API_BASE}/tables`)
        if (!res.ok) {
          throw new Error(await apiErrorMessage(res, "Failed to load tables"));
        }
        const data = await res.json()
        // data is expected to be something like ["customers","jobs",...]
        setTables(data)

        // Optionally set the first table as selected by default
        if (data.length > 0) {
          setSelectedTable(data[0])
        }
      } catch (err) {
        setError(err.message)
      }
    }
    fetchTables()
  }, [API_BASE])

  /**
   * Handle table selection from the dropdown.
   */
  function handleTableChange(e) {
    const val = e.target.value
    setSelectedTable(val)
    setFile(null)
    setPreviewData([])
    setPreviewHeaders([])
    setShowPreview(false)
    setMessage("")
    setError("")
  }

  /**
   * Handle CSV file selection; parse the first 20 rows for preview.
   */
  function handleFileChange(e) {
    const selectedFile = e.target.files[0] || null
    setFile(selectedFile)
    setPreviewData([])
    setPreviewHeaders([])
    setShowPreview(false)
    setMessage("")
    setError("")

    if (selectedFile) {
      Papa.parse(selectedFile, {
        header: true,
        skipEmptyLines: true,
        preview: 20,
        complete: (results) => {
          if (results.errors && results.errors.length > 0) {
            setError(`Error parsing CSV: ${results.errors[0].message}`)
          } else {
            setPreviewData(results.data)
            setPreviewHeaders(results.meta.fields || [])
            setShowPreview(true)
          }
        }
      })
    }
  }

  /**
   * Export CSV for the selected table.
   */
  async function handleExport() {
    setMessage("")
    setError("")
    if (!selectedTable) {
      setError("No table selected")
      return
    }
    try {
      const url = `${API_BASE}/export/${selectedTable}`
      const res = await fetch(url, { method: "GET" })
      if (!res.ok) {
        throw new Error(await apiErrorMessage(res, "Export failed"));
      }

      // Download the CSV
      const blob = await res.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = downloadUrl
      link.download = `${selectedTable}_export.csv`
      document.body.appendChild(link)
      link.click()
      link.remove()

      setMessage(`Exported table '${selectedTable}' successfully.`)
    } catch (err) {
      setError(`Export error: ${err.message}`)
    }
  }

  /**
   * Import CSV for the selected table.
   */
  async function handleImport() {
    setMessage("")
    setError("")
    if (!file) {
      setError("Please select a CSV file first.")
      return
    }
    if (!selectedTable) {
      setError("No table selected")
      return
    }
    try {
      const formData = new FormData()
      formData.append("file", file)

      const url = `${API_BASE}/import/${selectedTable}`
      const res = await fetch(url, {
        method: "POST",
        body: formData
      })
      if (!res.ok) {
        throw new Error(await apiErrorMessage(res, "Import failed"));
      }
      const data = await res.json()
      setMessage(
        `Import success for table '${selectedTable}'. ` +
        Object.entries(data)
              .map(([k, v]) => `${k}=${v}`)
              .join(", ")
      )

      // Optionally clear the preview after success
      setShowPreview(false)
      setPreviewData([])
      setPreviewHeaders([])
      setFile(null)
    } catch (err) {
      setError(`Import error: ${err.message}`)
    }
  }

  return (
    <div className="container my-4">
      <h2>Admin: Data Transfer (Import/Export CSV)</h2>

      <div className="card p-3">
        {/* Table Selection */}
        <div className="mb-3">
          <label className="form-label">Select a Table</label>
          <select
            className="form-select"
            value={selectedTable}
            onChange={handleTableChange}
          >
            {tables.map((tbl) => {
              // If /tables returns strings, tbl is a string.
              // If it returns objects, you might do tbl.value, tbl.label.
              return (
                <option key={tbl} value={tbl}>
                  {tbl}
                </option>
              )
            })}
          </select>
        </div>

        {/* Export Button */}
        <div className="mb-3">
          <button className="btn btn-primary me-2" onClick={handleExport}>
            Export CSV
          </button>
          <small className="text-muted">
            Download all rows from the selected table.
          </small>
        </div>

        {/* Import Section */}
        <div className="mb-3">
          <label className="form-label">Import CSV File</label>
          <input
            type="file"
            accept=".csv"
            className="form-control"
            onChange={handleFileChange}
          />

          {/* Optional Preview */}
          {showPreview && (
            <div className="mt-3">
              <h5>CSV Preview (first 20 rows)</h5>
              <div style={{ maxHeight: "300px", overflow: "auto" }}>
                <table className="table table-bordered table-sm">
                  <thead>
                    <tr>
                      {previewHeaders.map((header) => (
                        <th key={header}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.map((row, idx) => (
                      <tr key={idx}>
                        {previewHeaders.map((header) => (
                          <td key={header}>{row[header]}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Import Button */}
          <button className="btn btn-success mt-2" onClick={handleImport}>
            Confirm & Import CSV
          </button>
          <small className="text-muted d-block mt-1">
            Blank primary key will create new records; existing key will update.
            Full rollback if any row fails.
          </small>
        </div>
      </div>

      {/* Feedback Messages */}
      {message && <div className="alert alert-success mt-3">{message}</div>}
      {error && <div className="alert alert-danger mt-3">{error}</div>}
    </div>
  )
}
